import React from 'react';
import { Dialog } from '../reusable';
import { Hash, User, Calendar, Package, ShoppingCart } from 'lucide-react';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL');
};

const Field = ({ label, value, isDarkMode }) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: isDarkMode ? '#a1a1aa' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 14, color: isDarkMode ? '#f4f4f5' : '#0f172a', marginTop: 2, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const PurchaseReceiptDetailModal = ({
  isOpen,
  onClose,
  purchaseReceipt,
  isDarkMode = false,
  onEdit,
  onConfirm,
}) => {
  if (!purchaseReceipt) return null;
  const items = (Array.isArray(purchaseReceipt.items) ? purchaseReceipt.items : []).filter((item) => {
    const type = String(item?.item_type || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return ['materias primas', 'insumos', 'upselling'].includes(type);
  });

  const body = (
    <div style={{ padding: '0 20px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Recepción de compra
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>
            {purchaseReceipt.receipt_code || '—'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: isDarkMode ? '#d4d4d8' : '#475569' }}>
            {purchaseReceipt.purchase_order_code || 'Sin OC'}
            {purchaseReceipt.status ? (
              <span
                style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  background: isDarkMode ? '#27272a' : '#f1f5f9',
                  color: isDarkMode ? '#e4e4e7' : '#334155',
                }}
              >
                {purchaseReceipt.status}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px 24px' }}>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShoppingCart size={16} /> Orden
          </h4>
          <Field label="OC" value={purchaseReceipt.purchase_order_code} isDarkMode={isDarkMode} />
          <Field label="Proveedor" value={purchaseReceipt.supplier_name} isDarkMode={isDarkMode} />
          <Field label="Código proveedor" value={purchaseReceipt.supplier_code} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={16} /> Fechas
          </h4>
          <Field label="Fecha recepción" value={formatDate(purchaseReceipt.receipt_date)} isDarkMode={isDarkMode} />
          <Field label="Notas" value={purchaseReceipt.notes} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <User size={16} /> Responsable
          </h4>
          <Field label="Recibido por" value={purchaseReceipt.received_by} isDarkMode={isDarkMode} />
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Package size={16} /> Ítems ({items.length})
        </h4>
        {items.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin ítems.</p>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#27272a' : '#f8fafc' }}>
                  {['Tipo', 'Código', 'Descripción', 'Pedido', 'Recibido', 'Unidad'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '8px 10px',
                        textAlign: h === 'Pedido' || h === 'Recibido' ? 'right' : 'left',
                        fontSize: 11,
                        color: isDarkMode ? '#a1a1aa' : '#64748b',
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id || `${item.item_code}-${item.description}`} style={{ borderTop: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: isDarkMode ? '#d4d4d8' : '#475569' }}>{item.item_type || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: isDarkMode ? '#d4d4d8' : '#475569' }}>{item.item_code || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 13, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>{item.description || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right' }}>{item.quantity_ordered ?? 0}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>{item.quantity_received ?? 0}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{item.unit || 'UN'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {onEdit || onConfirm ? (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {onEdit ? (
          <button
            type="button"
            onClick={() => onEdit(purchaseReceipt)}
            style={{
              height: 40,
              padding: '0 18px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar recepción
          </button>
          ) : null}
          {onConfirm && String(purchaseReceipt.status || '').toLowerCase() === 'borrador' ? (
            <button
              type="button"
              onClick={() => onConfirm(purchaseReceipt)}
              style={{
                height: 40,
                padding: '0 18px',
                borderRadius: 8,
                border: 'none',
                background: '#0f766e',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Confirmar recepción
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Ficha de recepción"
      subtitle={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Hash size={14} /> Resumen</span>}
      isDarkMode={isDarkMode}
      size="lg"
    >
      {body}
    </Dialog>
  );
};

export default PurchaseReceiptDetailModal;
